import { BusinessObjectBase } from './base';
import { termTutorSchema } from '@schemas/business-objects/term-tutor';
import type { ActivityAssignment } from './activity-assignment';
import type { Attendance } from './attendance';
import type { NotAvailable } from './not-available';
import type { StudentAssignment } from './student-assignment';
import type { Term } from './term';
import type { Tutor } from './tutor';
import type { Id } from '../base-types';

export interface TermTutorData {
  readonly id: Id;
  readonly termId: Id;
  readonly term?: Term;
  readonly tutorId: Id;
  readonly tutor?: Tutor;
  readonly activityAssignments?: ActivityAssignment[];
  readonly studentAssignments?: StudentAssignment[];
  readonly notAvailables?: NotAvailable[];
  readonly attendances?: Attendance[];
}

export interface TermTutor extends Readonly<TermTutorData> {}
export class TermTutor extends BusinessObjectBase {
  static readonly entityName = 'TermTutor' as const;
  static readonly plural = 'term-tutors';
  static schema = termTutorSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<TermTutor[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new TermTutor(data));
  }

  static async findOne(filter?: any): Promise<TermTutor | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new TermTutor(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<TermTutor> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new TermTutor(data);
  }

  static async create(data: Omit<TermTutorData, 'id'>): Promise<TermTutor> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new TermTutor(result);
  }

  static async createMany(
    data: Omit<TermTutorData, 'id'>[],
  ): Promise<TermTutor[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new TermTutor(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<TermTutorData>,
  ): Promise<TermTutor> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new TermTutor(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
